import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './YourWeeklyWinsPage.module.css';
import { fetchQuickRichuelsAndCheckInHistory, handleApiError } from '../../utils/api';
import { getFirstNameFromStorage } from '../../utils/helper';
import ForecastProgressFooter from './forecast/ForecastProgressFooter';
import { SEE_ALL_MONEY_MOVES_PATH } from './your-quick-richuels/constants';
import {
  buildWeeklyRichuelReward,
  buildYourWeeklyWinsData,
  computeQuickRichuelImpactMetrics,
  getConfidenceChangeLabel,
  getWeeklyStressTrendLabel,
} from '../../utils/checkInUtils';
import { useContentCatalog } from '../../utils/contentCatalog';

const HOME_ROUTE = '/dashboard/forecast';
const STREAK_LENGTH = 7;
const CHART_DAYS = 7;
const Y_TICKS = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];
const STREAK_SLOTS = Array.from({ length: STREAK_LENGTH }, (_, i) => i);

const CHART = {
  width: 400,
  height: 200,
  padLeft: 36,
  padRight: 16,
  padTop: 16,
  padBottom: 32,
};
const CHART_PLOT_W = CHART.width - CHART.padLeft - CHART.padRight;
const CHART_PLOT_H = CHART.height - CHART.padTop - CHART.padBottom;
const CHART_RIGHT = CHART.width - CHART.padRight;
const CHART_BOTTOM = CHART.height - CHART.padBottom;

const cx = (...parts) => parts.filter(Boolean).join(' ');

function chartX(index) {
  return CHART.padLeft + (index / (CHART_DAYS - 1)) * CHART_PLOT_W;
}

function chartY(value) {
  const clamped = Math.max(1, Math.min(10, value));
  return CHART.padTop + CHART_PLOT_H - ((clamped - 1) / 9) * CHART_PLOT_H;
}

function buildLinePath(pts) {
  if (!pts?.length) return '';
  if (pts.length === 1) return `M ${pts[0][0]},${pts[0][1]}`;
  let path = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i += 1) {
    const [x1, y1] = pts[i - 1];
    const [x2, y2] = pts[i];
    path += ` Q ${(x1 + x2) / 2},${y1} ${x2},${y2}`;
  }
  return path;
}

function deriveWeeklyWinsView(payload, rewards) {
  const reflections = payload?.content_reflections ?? [];

  const report = buildYourWeeklyWinsData({
    checkInHistory: reflections,
  });

  const weeklyReward = buildWeeklyRichuelReward({
    streak: report.streak,
    weekRichuelCount: report.weekRichuelCount,
    metrics: computeQuickRichuelImpactMetrics({
      checkInHistory: reflections,
      v2OnboardingAiResult: payload?.v2_onboarding_ai_result ?? null,
    }),
    weeklyInsight: report.insight,
    rewards,
  });

  return { report, weeklyReward };
}

function TrendUpIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
      <path
        d="M4 16l5.5-5.5L13 14l7-7"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14 7h6v6"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  );
}

function TrophyIcon() {
  return (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
      <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />
    </svg>
  );
}

function StarMarker({ cx: x, cy, delayIndex = 0 }) {
  return (
    <g transform={`translate(${x - 10}, ${cy - 10})`}>
      <g className={styles.starMarker} style={{ '--star-delay': `${delayIndex * 80}ms` }}>
        <circle cx="10" cy="10" r="9" fill="#e8a838" stroke="#c8871a" strokeWidth="1" />
        <path
          d="M10 4.5l1.35 3.15L15 8.2l-2.65 2.1.85 3.45L10 12.1 6.8 13.75l.85-3.45L5 8.2l3.65-.55L10 4.5z"
          fill="#fff"
        />
      </g>
    </g>
  );
}

const WellnessChart = memo(function WellnessChart({ chartDays }) {
  const plotted = chartDays.map((day, i) => {
    const x = chartX(i);
    const confidenceY = chartY(day.confidence);
    const anxietyY = chartY(day.anxiety);
    return { day, i, x, confidenceY, anxietyY, starY: Math.min(confidenceY, anxietyY) - 16 };
  });

  return (
    <svg
      className={styles.chartSvg}
      viewBox={`0 0 ${CHART.width} ${CHART.height}`}
      role="img"
      aria-label="Confidence and anxiety over the last seven days"
    >
      {Y_TICKS.map((tick) => {
        const y = chartY(tick);
        return (
          <g key={tick}>
            <line
              x1={CHART.padLeft}
              y1={y}
              x2={CHART_RIGHT}
              y2={y}
              className={styles.chartGridLine}
            />
            <text x={8} y={y + 4} className={styles.chartAxisLabel}>
              {tick}
            </text>
          </g>
        );
      })}

      {plotted.map(({ day, x }) => (
        day.richuelCompleted ? (
          <line
            key={`richuel-line-${day.date}`}
            x1={x}
            y1={CHART.padTop}
            x2={x}
            y2={CHART_BOTTOM}
            className={styles.richuelMarkerLine}
          />
        ) : null
      ))}

      <path
        d={buildLinePath(plotted.map((p) => [p.x, p.confidenceY]))}
        pathLength="1"
        className={styles.chartLineConfidence}
        fill="none"
      />
      <path
        d={buildLinePath(plotted.map((p) => [p.x, p.anxietyY]))}
        pathLength="1"
        className={styles.chartLineAnxiety}
        fill="none"
      />

      {plotted.map(({ day, i, x, confidenceY, anxietyY, starY }) => (
        <g key={day.date}>
          <circle
            cx={x}
            cy={confidenceY}
            r={day.hasReflection ? 3.6 : 2.8}
            className={cx(
              styles.chartPoint,
              styles.chartPointConfidence,
              !day.hasReflection && styles.chartPointEstimated,
            )}
            style={{ '--point-delay': `${i * 70}ms` }}
          />
          <circle
            cx={x}
            cy={anxietyY}
            r={day.hasReflection ? 3.6 : 2.8}
            className={cx(
              styles.chartPoint,
              styles.chartPointAnxiety,
              !day.hasReflection && styles.chartPointEstimated,
            )}
            style={{ '--point-delay': `${i * 70}ms` }}
          />
          {day.richuelCompleted ? <StarMarker cx={x} cy={starY} delayIndex={i} /> : null}
          <text
            x={x}
            y={CHART.height - 8}
            textAnchor="middle"
            className={styles.chartDayLabel}
          >
            {day.label}
          </text>
        </g>
      ))}
    </svg>
  );
});

function PageFrame({ children }) {
  return (
    <div className={styles.yourWeeklyWinsPage} data-page="your-weekly-wins">
      {children}
    </div>
  );
}

function LoadingState() {
  return (
    <PageFrame>
      <div className={styles.loadingState} aria-busy="true" aria-live="polite">
        <span className={styles.loadingSpinner} aria-hidden="true">
          <span className={styles.loadingSpinnerInner} />
        </span>
        <p className={styles.loadingText}>Preparing your weekly wins…</p>
      </div>
    </PageFrame>
  );
}

function ErrorState({ onRetry, onGoHome }) {
  return (
    <PageFrame>
      <div className={styles.mainContent}>
        <section className={styles.errorState} aria-live="polite">
          <h1 className={styles.winsTitle}>Your Weekly Wins</h1>
          <p className={styles.errorText}>
            We couldn&apos;t load your weekly wins right now. Check your connection and try again.
          </p>
          <div className={styles.errorActions}>
            <button type="button" className={styles.retryBtn} onClick={onRetry}>
              Try again
            </button>
            <button type="button" className={styles.finishBtn} onClick={onGoHome}>
              Go to Home
            </button>
          </div>
        </section>
      </div>
    </PageFrame>
  );
}

function YourWeeklyWinsPage() {
  const navigate = useNavigate();
  const { catalog } = useContentCatalog();
  const firstName = getFirstNameFromStorage() || 'there';
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [reportPayload, setReportPayload] = useState(null);

  useEffect(() => {
    const ac = new AbortController();
    const { signal } = ac;

    const run = async () => {
      setLoading(true);
      setLoadError(false);

      try {
        const result = await fetchQuickRichuelsAndCheckInHistory(signal);
        if (signal.aborted) return;
        if (!result) {
          setLoadError(true);
          setReportPayload(null);
          return;
        }
        setReportPayload(result);
      } catch (e) {
        if (e?.name === 'AbortError' || signal.aborted) return;
        handleApiError(e, navigate, false);
        setLoadError(true);
        setReportPayload(null);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    run();
    return () => ac.abort();
  }, [navigate, reloadKey]);

  const derived = useMemo(
    () => (reportPayload ? deriveWeeklyWinsView(reportPayload, catalog?.weekly_rewards) : null),
    [reportPayload, catalog?.weekly_rewards],
  );

  const handleGoHome = useCallback(() => {
    navigate(HOME_ROUTE);
  }, [navigate]);

  const handleRetry = useCallback(() => {
    setReloadKey((key) => key + 1);
  }, []);

  if (loading) return <LoadingState />;
  if (loadError || !derived) return <ErrorState onRetry={handleRetry} onGoHome={handleGoHome} />;

  const { report, weeklyReward } = derived;
  const { insight, chartDays } = report;
  const graphStats = [
    { label: 'Richuel days', value: `${report.weekRichuelCount}/7` },
    { label: 'Reflections', value: report.weekReflectionCount },
    {
      label: 'Stress trend',
      value: getWeeklyStressTrendLabel({
        anxietyDropPct: insight.anxietyDropPct,
        chartDays,
      }),
    },
    { label: 'Confidence', value: getConfidenceChangeLabel(insight.confidenceChange) },
  ];
  const streakSegments = Math.min(report.streak, STREAK_LENGTH);
  const hasEstimatedChartPoints = chartDays.some((day) => !day.hasReflection);

  return (
    <PageFrame>
      <div className={styles.mainContent}>
        <header className={styles.winsHeader}>
          <h1 className={styles.winsTitle}>Your Weekly Wins</h1>
          <p className={styles.winsSubtitle}>
            Your progress overview for
            {' '}
            {report.weekRangeLabel}
          </p>
        </header>

        <section className={styles.insightCard} aria-label="Weekly insight">
          <div className={styles.insightIconWrap}>
            <TrendUpIcon />
          </div>
          <div className={styles.insightBody}>
            <span className={styles.insightKicker}>Your biggest weekly pattern</span>
            <h2 className={styles.insightTitle}>{insight.title}</h2>
            <p className={styles.insightText}>{insight.sentence}</p>
            <p className={styles.insightDetail}>{insight.sourceNote}</p>
          </div>
        </section>

        <section className={styles.graphCard} aria-label="Wellness correlation">
          <div className={styles.graphCardHeader}>
            <div>
              <h2 className={styles.graphTitle}>Emotion Graph</h2>
              <p className={styles.graphSubtitle}>
                Confidence and financial anxiety over the last 7 days.
              </p>
            </div>
            <ul className={styles.legend}>
              <li>
                <span className={cx(styles.legendDot, styles.legendConfidence)} />
                Confidence
              </li>
              <li>
                <span className={cx(styles.legendDot, styles.legendAnxiety)} />
                Anxiety
              </li>
              <li>
                <span className={styles.legendStar} aria-hidden="true">★</span>
                Completed Richuels
              </li>
            </ul>
          </div>

          <div className={styles.chartWrap}>
            <WellnessChart chartDays={chartDays} />
          </div>

          {hasEstimatedChartPoints && (
            <p className={styles.chartNote}>
              Smaller dots are estimated from nearby reflections. Reflecting after each finished Quick Richuel makes this graph more accurate.
            </p>
          )}
        </section>

        <div className={styles.graphStats} aria-label="Weekly wins stats">
          {graphStats.map((stat) => (
            <div key={stat.label} className={styles.graphStat}>
              <span>{stat.label}</span>
              <strong>{stat.value}</strong>
            </div>
          ))}
        </div>

        {report.showRichuelInsight && (
          <div className={styles.richuelInsightBanner}>
            <InfoIcon />
            <p>
              <strong>Richuel Connection:</strong>
              {' '}
              {insight.detail}
            </p>
          </div>
        )}

        <div className={styles.statsRow}>
          <section className={styles.streakCard} aria-label="Active streak">
            <span className={styles.statLabel}>ACTIVE STREAK</span>
            <p className={styles.streakValue}>
              <span className={styles.streakNumber}>{report.streak}</span>
              {' '}
              {report.streak === 1 ? 'Day' : 'Days'}
              {' '}
              Consistent
            </p>
            <div className={styles.streakBar} role="presentation">
              {STREAK_SLOTS.map((i) => (
                <span
                  key={i}
                  className={cx(styles.streakSegment, i < streakSegments && styles.streakSegmentFilled)}
                  style={{ '--bar-delay': `${i * 45}ms` }}
                />
              ))}
            </div>
          </section>

          <section
            className={cx(styles.rewardCard, !weeklyReward.isEarned && styles.rewardCardPending)}
            aria-label={weeklyReward.isEarned ? 'Reward earned' : 'Next reward'}
          >
            <span className={styles.rewardLabel}>{weeklyReward.label}</span>
            <div className={styles.rewardContent}>
              <TrophyIcon />
              <div>
                <p className={styles.rewardTitle}>{weeklyReward.title}</p>
                <p className={styles.rewardSubtitle}>{weeklyReward.subtitle}</p>
                {weeklyReward.detail ? (
                  <p className={styles.rewardDetail}>{weeklyReward.detail}</p>
                ) : null}
                {weeklyReward.nextMilestone ? (
                  <p className={styles.rewardMilestone}>{weeklyReward.nextMilestone}</p>
                ) : null}
              </div>
            </div>
          </section>
        </div>

        <div className={styles.footerReveal}>
          <ForecastProgressFooter
            title="Keep going!"
            subtitle={`Every small action compounds into a brighter tomorrow. You're doing great, ${firstName}!`}
            onStartMove={() => navigate(SEE_ALL_MONEY_MOVES_PATH)}
            ariaLabel="Keep going"
          />
        </div>
      </div>
    </PageFrame>
  );
}

export default YourWeeklyWinsPage;
